import React, { useEffect, useMemo } from 'react';
import BoardFormSkeleton from '@/components/Board/CreateBoardModal/BoardForm/BoardFormSkeleton';
import { Box, Stack } from '@mui/material';
import {
  FormConfig,
  FormConfigBody,
  FormConfigHeader,
  FormConfigHeaderTitle,
  FormGroup,
} from '@/components/Board/CreateBoardModal/BoardForm/BoardFormField/style';
import Label from '@/components/@IntranetLibrary/Label';
import RHFMultiAutoCompleteHighLightsSelect from '@/components/HookForm/RHFMultiAutoCompleteHighLightsSelect';
import RHFTextField from '@/components/HookForm/RHFTextField';
import ImageSlide from '@/components/Board/CreateBoardModal/BoardForm/BoardFormField/ImageSlide';
import FileAttachment from '@/components/Board/CreateBoardModal/BoardForm/BoardFormField/FileAttachment';
import SingleZipFileUpload from '@/components/Board/CreateBoardModal/BoardForm/BoardFormField/SingleZipFileUpload';
import EditorIcon from '@/components/Board/CreateBoardModal/BoardForm/BoardFormField/FormTypeTab/EditorIcon';
import CodeIcon from '@/components/Board/CreateBoardModal/BoardForm/BoardFormField/FormTypeTab/CodeIcon';
import ContentTab from '@/components/ContentTab';
import { BoardFormData, BooleanOption } from '@/hooks/board/useBoardForm';
import { useTranslation } from 'react-i18next';
import { BoardFormOptions } from '@/hooks/board/useBoardFormOptions';
import { FormProviderProps } from '@/components/HookForm/FormProvider';
import ChevronDownIcon from '@/public/icons/editor/chevron-down.svg';
import DisplaySections from '@/components/Board/CreateBoardModal/BoardForm/BoardFormField/DisplaySections';
import RHFImageOptionSelector from '@/components/HookForm/RHFImageOptionSelector';
import RHFRadio from '@/components/HookForm/RHFRadio';
import RHFDateTimePicker from '@/components/HookForm/RHFDateTimePicker';
import { useRouter } from 'next/router';
import RHFLexicalEditor from '@/components/HookForm/RHFLexicalEditor';
import { PAGE_URL } from '@/constant/url';
import { useAtomValue } from 'jotai';
import { MyInfoState } from '@/recoil/atom';

export type BoardFormProps = {
  isLoading: boolean;
  options: BoardFormOptions;
} & Pick<FormProviderProps<BoardFormData>, 'methods'>;

const BoardForm = (props: BoardFormProps) => {
  const { isLoading, options, methods } = props;
  const { t } = useTranslation();
  const router = useRouter();
  const userInfo = useAtomValue(MyInfoState);

  const {
    categoryOptions,
    displaySectionOptions,
    commentPositionOptions,
    uploadTimeOptions,
    importantOptions,
  } = options;

  const { watch, setValue } = methods;
  const values = watch();
  const category = !values.categories
    ? []
    : values.categories
        .map((c) => categoryOptions.find((n) => n.value === c))
        .filter(
          (item): item is { value: string; label: string } =>
            item !== undefined,
        );

  const handleFormTypeChange = (formType: 'editor' | 'codeUpload') => {
    setValue('formType', formType, { shouldValidate: true });
  };

  const tabData = useMemo(
    () => [
      {
        value: 'editor' as const,
        title: '에디터 작성',
        icon: (
          <EditorIcon
            color={values.formType === 'editor' ? '#4D5157' : '#888D96'}
          />
        ),
      },
      {
        value: 'codeUpload' as const,
        title: '코드 업로드',
        icon: (
          <CodeIcon
            color={values.formType === 'codeUpload' ? '#4D5157' : '#888D96'}
          />
        ),
      },
    ],
    [values.formType],
  );

  // codeZipFile이 변경되면 axLoungeDeploy 업데이트
  useEffect(() => {
    if (values.codeZipFile?.uuid) {
      setValue('axLoungeDeploy', {
        fileUuid: values.codeZipFile.uuid,
        version: '1.0.0',
      });
    }
  }, [values.codeZipFile, setValue]);

  return isLoading ? (
    <BoardFormSkeleton />
  ) : (
    <>
      {router.pathname.startsWith(PAGE_URL.HOME_MOBILE) && (
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, shrink-to-fit=no, maximum-scale=1, user-scalable=no"
        />
      )}

      <Box>
        <Stack spacing={5}>
          <FormGroup gapsize={'s'}>
            <Label
              isRequired={true}
              title={t('카테고리')}
              description={t('여러 개를 선택할 수도 있어요.')}
              size={16}
            />
            <RHFMultiAutoCompleteHighLightsSelect
              name={'categories'}
              options={categoryOptions}
              labelKey={'label'}
              valueKey={'value'}
              placeholder={'선택'}
              value={category}
            />
          </FormGroup>

          <FormGroup gapsize={'s'}>
            <ContentTab
              items={tabData}
              value={values.formType}
              onChange={handleFormTypeChange}
              fullWidth
              size="medium"
            />
          </FormGroup>

          <FormGroup gapsize={'s'}>
            <Label
              isRequired={true}
              title={values.formType === 'editor' ? '제목' : '프로젝트명'}
              size={16}
            />
            <RHFTextField
              name={'subject'}
              placeholder={
                values.formType === 'editor'
                  ? t('제목을 입력해 주세요.')
                  : t('프로젝트명을 입력해 주세요.')
              }
              variant={'outlined'}
              maxLength={100}
            />
          </FormGroup>

          {values.formType === 'editor' ? (
            <>
              <FormGroup gapsize={'s'}>
                <Label isRequired={false} title={'내용'} size={16} />
                <RHFLexicalEditor name={'content'} />
                <ImageSlide />
              </FormGroup>
              <FormGroup gapsize={'s'}>
                <Label
                  isRequired={false}
                  title={'파일첨부'}
                  description={
                    '파일은 개당 최대 500MB까지 첨부할 수 있어요. (총 개수나 총 용량 제한은 없어요.)'
                  }
                  size={16}
                />
                <FileAttachment name="attachments" />
              </FormGroup>
            </>
          ) : (
            <>
              <FormGroup gapsize={'s'}>
                <Label
                  isRequired={true}
                  title={'결과물 파일 (.zip, .html)'}
                  description={'파일은 최대 500MB까지 첨부할 수 있어요.'}
                  size={16}
                />
                <SingleZipFileUpload
                  name="codeZipFile"
                  hideDropzone={!!values.codeZipFile}
                />
              </FormGroup>
              <FormGroup gapsize={'s'}>
                <Label
                  isRequired={false}
                  title={'파일첨부'}
                  description={
                    '파일은 개당 최대 500MB까지 첨부할 수 있어요. (총 개수나 총 용량 제한은 없어요.)'
                  }
                  size={16}
                />
                <FileAttachment name="attachments" />
              </FormGroup>
            </>
          )}
        </Stack>
        <FormConfig defaultExpanded>
          <FormConfigHeader
            expandIcon={
              <ChevronDownIcon
                width={'28px'}
                height={'28px'}
                style={{ transform: 'rotate(-180deg)' }}
              />
            }
            aria-controls="board-config-content"
            id="board-config-header"
          >
            <FormConfigHeaderTitle>게시글 설정</FormConfigHeaderTitle>
          </FormConfigHeader>
          <FormConfigBody>
            <FormGroup gapsize={'m'}>
              <DisplaySections options={displaySectionOptions} />
            </FormGroup>
            <FormGroup gapsize={'m'}>
              <Label
                isRequired={false}
                title={t('코멘트가 보여질 위치를 선택해 주세요.')}
                description={
                  <>
                    콘텐츠가 중요한 글이라면 ‘게시글 아래’, 반응이 중요한
                    글이라면 ‘게시글 오른쪽’을 추천드려요. <br />
                    모바일에서는 설정과 관계없이 게시글 아래로 표현됩니다.
                  </>
                }
                size={16}
              />
              <RHFImageOptionSelector
                name="commentPosition"
                options={commentPositionOptions}
              />
            </FormGroup>
            <FormGroup gapsize={'m'}>
              <Label
                isRequired={false}
                title={'게시글을 언제 올리실 건가요?'}
                description={
                  '‘예약하기’를 선택하면 설정한 시간에 게시글이 등록됩니다.'
                }
                size={16}
              />
              <RHFRadio name={'useUploadTime'} options={uploadTimeOptions} />
              {values.useUploadTime === BooleanOption.TRUE && (
                <RHFDateTimePicker name={'displayTime'} />
              )}
            </FormGroup>
            <FormGroup gapsize={'m'}>
              <Label
                isRequired={false}
                title={'중요 게시글로 설정할건가요?'}
                description={'중요 게시글은 제목에 강조 효과가 적용됩니다.'}
                size={16}
              />
              <RHFRadio name={'isImportant'} options={importantOptions} />
            </FormGroup>
          </FormConfigBody>
        </FormConfig>
      </Box>
    </>
  );
};

export default BoardForm;
